/**
 * The catalog behind the three karaktärsval: what each choice costs, what it
 * is called, how much of it you buy, and how it looks.
 *
 * One table per choice, and every consumer reads it — the BOM, the price
 * deltas, the build guide, the timeline and the 3D material library. A second
 * copy of "aspen is the pale one" or "slamfärg needs two coats" anywhere else
 * is a bug waiting to happen (see the mirrored-constants note).
 */
import type {
	ExteriorTreatmentId,
	InteriorPanelDirection,
	InteriorWoodSpecies,
} from "./types";

/** Colour/sheen overrides the viewer stamps onto a shared wood material. */
export interface FinishSurfaceLook {
	/** Multiplied over the albedo — see the texture-swap/profile-tint note. */
	tint: number;
	roughness: number;
}

export interface InteriorWoodSpec {
	/**
	 * The Swedish word appended to the BOM row's `material`, e.g.
	 * `"Bastupanel, asp"`. Raw engine strings stay Swedish stable IDs;
	 * `translateMaterial` strips and re-translates this suffix at the render
	 * boundary (see the intl-leak coverage note).
	 */
	bomWordSv: string;
	/** Dictionary key for the species name in the UI and in translated BOM rows. */
	labelKey: "finish_wood_asp" | "finish_wood_al" | "finish_wood_gran";
	descriptionKey:
		| "finish_wood_aspDescription"
		| "finish_wood_alDescription"
		| "finish_wood_granDescription";
	/** Price rule for 15×90 interior panel in this species. */
	panelPriceRuleId: string;
	/** Price rule for 28×90 lavvirke (bench slats + front board) in this species. */
	benchPriceRuleId: string;
	/** Swatch colour for the UI chip (not the 3D tint — that is `panelLook`). */
	swatchHex: string;
	panelLook: FinishSurfaceLook;
	benchLook: FinishSurfaceLook;
}

/**
 * The material profiles read their default tint and roughness from this table.
 * Species changes and quality swaps therefore restore the same surface finish.
 */
export const INTERIOR_WOOD_CATALOG: Record<
	InteriorWoodSpecies,
	InteriorWoodSpec
> = {
	asp: {
		bomWordSv: "asp",
		labelKey: "finish_wood_asp",
		descriptionKey: "finish_wood_aspDescription",
		panelPriceRuleId: "prices.bastupanelAsp",
		benchPriceRuleId: "prices.lavvirke",
		swatchHex: "#f0e3cb",
		panelLook: { tint: 0xebdbc0, roughness: 0.72 },
		benchLook: { tint: 0xeee0c6, roughness: 0.54 },
	},
	al: {
		bomWordSv: "al",
		labelKey: "finish_wood_al",
		descriptionKey: "finish_wood_alDescription",
		panelPriceRuleId: "prices.bastupanelAl",
		benchPriceRuleId: "prices.lavvirkeAl",
		swatchHex: "#d9a071",
		// Klibbal darkens to a warm red-brown; lower roughness because alder
		// planes to a noticeably smoother, slightly waxy surface than aspen.
		panelLook: { tint: 0xdca97e, roughness: 0.56 },
		benchLook: { tint: 0xe0b184, roughness: 0.46 },
	},
	gran: {
		bomWordSv: "gran",
		labelKey: "finish_wood_gran",
		descriptionKey: "finish_wood_granDescription",
		panelPriceRuleId: "prices.bastupanel",
		benchPriceRuleId: "prices.lavvirkeGran",
		swatchHex: "#e8d5a4",
		// Spruce reads yellower and a touch duller than aspen, and it is the
		// budget option — this is the look the BOM has actually been pricing.
		panelLook: { tint: 0xe9d5a6, roughness: 0.68 },
		benchLook: { tint: 0xecdcb0, roughness: 0.56 },
	},
};

export interface ExteriorTreatmentSpec {
	/** Raw Swedish BOM row name — a stable pricing ID like every other. */
	bomNameSv: string;
	labelKey:
		| "finish_treatment_untreated"
		| "finish_treatment_tjarvitriol"
		| "finish_treatment_jarnvitriol"
		| "finish_treatment_slamfargRed"
		| "finish_treatment_slamfargBlack";
	descriptionKey:
		| "finish_treatment_untreatedDescription"
		| "finish_treatment_tjarvitriolDescription"
		| "finish_treatment_jarnvitriolDescription"
		| "finish_treatment_slamfargRedDescription"
		| "finish_treatment_slamfargBlackDescription";
	/** null for `untreated` — nothing is bought, so there is no row. */
	priceRuleId: string | null;
	/** Manufacturer spread rate on planed softwood, m² per litre per coat. */
	coverageSqmPerLitre: number;
	coats: number;
	/**
	 * Hours the facade must stand before the build can continue (last coat →
	 * handover). Slamfärg's figure covers the wait BETWEEN its two coats too.
	 */
	dryingHours: number;
	swatchHex: string;
	/**
	 * null for `untreated`, which does not restyle anything: each facade
	 * material restores ITS OWN tint (the trim family sits deliberately
	 * lighter/darker than the cladding), so there is no single "untreated
	 * colour" to state here.
	 */
	look: FinishSurfaceLook | null;
}

/**
 * Coverage and coat counts are manufacturer figures for planed spruce
 * cladding (Auson tjärvitriol ~7 m²/l, iron-sulphate wash ~9 m²/l, Falu
 * slamfärg ~4.5 m²/l/coat × 2 coats) — verified against product data sheets,
 * not against another function in this repo.
 *
 * `untreated` carries no look at all — every facade material restores its own
 * tint, which is what keeps the default path visually unchanged.
 */
export const EXTERIOR_TREATMENT_CATALOG: Record<
	ExteriorTreatmentId,
	ExteriorTreatmentSpec
> = {
	untreated: {
		bomNameSv: "Obehandlad fasad",
		labelKey: "finish_treatment_untreated",
		descriptionKey: "finish_treatment_untreatedDescription",
		priceRuleId: null,
		coverageSqmPerLitre: 0,
		coats: 0,
		dryingHours: 0,
		swatchHex: "#cbbda6",
		look: null,
	},
	tjarvitriol: {
		bomNameSv: "Tjärvitriol (fasadbehandling)",
		labelKey: "finish_treatment_tjarvitriol",
		descriptionKey: "finish_treatment_tjarvitriolDescription",
		priceRuleId: "prices.tjarvitriol",
		coverageSqmPerLitre: 7,
		coats: 1,
		dryingHours: 24,
		swatchHex: "#6f5637",
		look: { tint: 0x6d5539, roughness: 0.84 },
	},
	jarnvitriol: {
		bomNameSv: "Järnvitriol (fasadbehandling)",
		labelKey: "finish_treatment_jarnvitriol",
		descriptionKey: "finish_treatment_jarnvitriolDescription",
		priceRuleId: "prices.jarnvitriol",
		coverageSqmPerLitre: 9,
		coats: 1,
		// Touch-dry in hours; the silvering that follows takes months of
		// weather and is not build time, so it does not belong in the timeline.
		dryingHours: 6,
		swatchHex: "#9a958c",
		look: { tint: 0x9c968b, roughness: 0.9 },
	},
	"slamfarg-red": {
		bomNameSv: "Slamfärg röd (fasadbehandling)",
		labelKey: "finish_treatment_slamfargRed",
		descriptionKey: "finish_treatment_slamfargRedDescription",
		priceRuleId: "prices.slamfarg",
		coverageSqmPerLitre: 4.5,
		coats: 2,
		dryingHours: 24,
		swatchHex: "#8f3a2a",
		// Slamfärg is a chalky distemper — almost no specular at all.
		look: { tint: 0x8d3b2b, roughness: 0.96 },
	},
	"slamfarg-black": {
		bomNameSv: "Slamfärg svart (fasadbehandling)",
		labelKey: "finish_treatment_slamfargBlack",
		descriptionKey: "finish_treatment_slamfargBlackDescription",
		priceRuleId: "prices.slamfarg",
		coverageSqmPerLitre: 4.5,
		coats: 2,
		dryingHours: 24,
		swatchHex: "#2e2b28",
		look: { tint: 0x322f2b, roughness: 0.96 },
	},
};

export interface PanelDirectionSpec {
	labelKey: "finish_direction_standing" | "finish_direction_lying";
	descriptionKey:
		| "finish_direction_standingDescription"
		| "finish_direction_lyingDescription";
}

export const PANEL_DIRECTION_CATALOG: Record<
	InteriorPanelDirection,
	PanelDirectionSpec
> = {
	standing: {
		labelKey: "finish_direction_standing",
		descriptionKey: "finish_direction_standingDescription",
	},
	lying: {
		labelKey: "finish_direction_lying",
		descriptionKey: "finish_direction_lyingDescription",
	},
};

/**
 * The `", <species>"` suffix `applyInteriorWoodToRows` writes onto an
 * interior-wood row's `material`, and `translateMaterial` strips and
 * re-translates at the render boundary.
 *
 * BUILT FROM THE CATALOG, not spelled out: a hand-written copy in the i18n
 * layer is exactly how a fourth wood would ship a Swedish word to intl —
 * `asp`, `al` and `gran` are pure ASCII, so the å/ä/ö sweep cannot see it.
 * One owner means one row in the catalog is genuinely all a new species needs.
 */
export const SPECIES_SUFFIX_RE = new RegExp(
	`,\\s*(${Object.values(INTERIOR_WOOD_CATALOG)
		.map((spec) => spec.bomWordSv)
		.join("|")})$`,
);

/** Species id for a `bomWordSv`, or null when the name carries no suffix. */
export function readSpeciesSuffix(
	materialName: string,
): InteriorWoodSpecies | null {
	const word = materialName.match(SPECIES_SUFFIX_RE)?.[1];
	if (!word) return null;
	return (
		(Object.keys(INTERIOR_WOOD_CATALOG) as InteriorWoodSpecies[]).find(
			(id) => INTERIOR_WOOD_CATALOG[id].bomWordSv === word,
		) ?? null
	);
}

/** Every BOM row whose material IS the chosen interior wood, by `materialKey`. */
export const INTERIOR_WOOD_PANEL_KEYS: readonly string[] = [
	"interior.sauna-panel",
	"interior.relax-panel",
	"interior.ceiling-panel",
	"interior.ceiling-panel.sauna",
	"interior.ceiling-panel.relax",
];

/**
 * Bench rows in the chosen wood. Deliberately NOT `interior.bench-ledger` or
 * `interior.bench-frame`: those are hidden construction lumber priced as
 * `prices.vaggreglar`, and buying knot-free aspen for a bench leg you never
 * see is exactly the kind of thing this BOM should not do.
 */
export const INTERIOR_WOOD_BENCH_KEYS: readonly string[] = [
	"interior.bench-timber",
	"interior.bench-front",
];
